import { createContext, forwardRef, useContext, useEffect, useMemo, useRef, useState, type AnchorHTMLAttributes, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { productSiteOrigin, productSiteUrl } from "../config/siteOrigin";
import { getMarketingConsent } from "./consent";
import { emitMarketingEvent, type MarketingReporter } from "./marketingEvents";
import { createMarketingTransition } from "./transitionEnvelope";
import { flushMarketingCtaActivations, recordMarketingCtaActivation } from "./clientDeliveryAudit";
import type { MarketingConsent } from "./consent";

type Uuid = () => `${string}-${string}-${string}-${string}-${string}`;
type RuntimeOptions = {
  report?: MarketingReporter;
  artifactSha?: string;
  uuid?: Uuid;
  auditActivation?: (activation: { consentState: MarketingConsent; transitionId?: string }) => void;
};
type AnalyticsContextValue = RuntimeOptions & { pageViewId: string | null };
const AnalyticsContext = createContext<AnalyticsContextValue>({ pageViewId: null });
const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const;
const UTM_VALUE = /^[A-Za-z0-9._-]{1,100}$/;
const defaultUuid: Uuid = () => crypto.randomUUID();

export const marketingDomReporter: MarketingReporter = (event, parameters) => {
  window.dispatchEvent(new CustomEvent("pinpoint:marketing-analytics", { detail: { event, parameters } }));
};

function classifyReferrer(): "direct" | "internal" | "search" | "social" | "campaign" | "other" {
  if (new URLSearchParams(location.search).has("utm_source")) return "campaign";
  if (!document.referrer) return "direct";
  try {
    const host = new URL(document.referrer).hostname.toLowerCase();
    if (host === location.hostname.toLowerCase()) return "internal";
    if (/google|bing|duckduckgo|yahoo/.test(host)) return "search";
    if (/facebook|instagram|linkedin|tiktok|x\.com|twitter/.test(host)) return "social";
  } catch { return "other"; }
  return "other";
}

function acquisitionFields() {
  const params = new URLSearchParams(location.search);
  const utm: Partial<Record<typeof UTM_KEYS[number], string>> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(`utm_${key}`);
    if (value && UTM_VALUE.test(value)) utm[key] = value;
  }
  return { referrerClass: classifyReferrer(), utm };
}

export function MarketingAnalyticsOwner({ children, report = marketingDomReporter, artifactSha = import.meta.env.VITE_ARTIFACT_SHA ?? "", uuid = defaultUuid, auditActivation = recordMarketingCtaActivation }: RuntimeOptions & { children?: ReactNode }) {
  const route = useLocation();
  const [pageViewId, setPageViewId] = useState<string | null>(null);

  useEffect(() => { flushMarketingCtaActivations(); }, []);

  useEffect(() => {
    const consent = getMarketingConsent();
    if (consent !== "granted" || !/^[0-9a-f]{64}$/.test(artifactSha)) {
      setPageViewId(null);
      return;
    }
    const nextPageViewId = uuid();
    setPageViewId(nextPageViewId);
    emitMarketingEvent("public_page_view", {
      pageViewId: nextPageViewId,
      canonicalPath: route.pathname,
      ...acquisitionFields(),
      consentState: "granted",
    }, { consent, artifactSha, report, uuid });
  }, [artifactSha, report, route.pathname, uuid]);

  return <AnalyticsContext.Provider value={{ pageViewId, report, artifactSha, uuid, auditActivation }}>{children}</AnalyticsContext.Provider>;
}

type MarketingProductLinkProps = {
  ctaId: string;
  path?: "/login" | "/practice";
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

export const MarketingProductLink = forwardRef<HTMLAnchorElement, MarketingProductLinkProps>(function MarketingProductLink(
  { ctaId, path = "/login", children, onClick, ...anchorProps }, ref,
) {
  const context = useContext(AnalyticsContext);
  const element = useRef<HTMLAnchorElement | null>(null);
  const consent = getMarketingConsent();
  const transition = useMemo(() => createMarketingTransition({
    consent, destinationPath: path, productOrigin: productSiteOrigin,
    utm: acquisitionFields().utm, uuid: context.uuid,
  }), [consent, context.uuid, path]);
  const href = transition?.href ?? productSiteUrl(path);

  useEffect(() => {
    if (!context.pageViewId || !context.report || !context.artifactSha) return;
    let emitted = false;
    const emit = () => {
      if (emitted) return;
      emitted = true;
      emitMarketingEvent("product_cta_impression", {
        pageViewId: context.pageViewId!, ctaId, consentState: "granted", transitionId: transition?.transitionId,
      }, { consent, artifactSha: context.artifactSha!, report: context.report!, uuid: context.uuid });
    };
    if (typeof IntersectionObserver === "undefined" || !element.current) {
      emit();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        emit();
        observer.disconnect();
      }
    });
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [consent, context, ctaId, transition?.transitionId]);

  return <a {...anchorProps} ref={(node) => {
    element.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }} href={href} onClick={(event) => {
    context.auditActivation?.({
      consentState: consent,
      ...(transition?.transitionId ? { transitionId: transition.transitionId } : {}),
    });
    if (context.pageViewId && context.report && context.artifactSha) {
      emitMarketingEvent("product_cta_clicked", {
        pageViewId: context.pageViewId, ctaId, destinationHost: "app", destinationPath: path,
        consentState: "granted", transitionId: transition?.transitionId,
      }, { consent, artifactSha: context.artifactSha, report: context.report, uuid: context.uuid });
    }
    onClick?.(event);
  }}>{children}</a>;
});
